import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { matchService } from '../../services/matchService';
import { tournamentService } from '../../services/tournamentService';
import PlayerMatchStatsRecorder from './components/PlayerMatchStatsRecorder';

const MATCH_TYPE_OPTIONS = [
  { value: 'league', label: 'League Matches' },
  { value: 'tournament', label: 'Tournament Games' },
];

const MatchesUpdatesPage = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [matchType, setMatchType] = useState('league');
  const [selectedGameId, setSelectedGameId] = useState('');
  const [fixtures, setFixtures] = useState([]);
  const [fixturesLoading, setFixturesLoading] = useState(false);
  const [fixturesError, setFixturesError] = useState(null);
  const [fixtureDate, setFixtureDate] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [editForm, setEditForm] = useState({
    homeScore: '',
    awayScore: '',
    status: 'completed',
    notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [banner, setBanner] = useState(null);

  const breadcrumbItems = [
    { label: 'Admin Dashboard', path: '/admin-dashboard' },
    { label: 'Match Operations', path: '/match-management' },
    { label: 'Matches Updates' },
  ];

  const formatFixtureDate = (isoDate) => {
    if (!isoDate) return 'Date & time TBA';
    const date = new Date(isoDate);
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${formattedDate} • ${formattedTime}`;
  };

  const normalizeLeagueFixture = (fixture) => ({
    id: fixture.id,
    matchType: 'league',
    homeName: fixture?.homeTeam?.name || fixture?.homeTeam?.shortName || 'Home Team',
    awayName: fixture?.awayTeam?.name || fixture?.awayTeam?.shortName || 'Away Team',
    homeTeamId: fixture?.homeTeam?.id || fixture?.homeTeamId || null,
    awayTeamId: fixture?.awayTeam?.id || fixture?.awayTeamId || null,
    leagueId: fixture?.leagueId || null,
    scheduledDate: fixture?.scheduledDate,
    venue: fixture?.venue,
    homeScore: fixture?.homeScore,
    awayScore: fixture?.awayScore,
    status: fixture?.matchStatus || 'scheduled',
    notes: fixture?.matchNotes || '',
    label:
      fixture?.roundName ||
      (fixture?.league?.name
        ? `${fixture.league.name}${fixture?.league?.season ? ` • ${fixture.league.season}` : ''}`
        : 'League Fixture'),
  });

  const normalizeTournamentFixture = (game) => ({
    id: game.id,
    matchType: 'tournament',
    homeName: game?.homeTeam?.teamName || 'Home Team',
    awayName: game?.awayTeam?.teamName || 'Away Team',
    homeTeamId: game?.homeTeamId || game?.homeTeam?.id || null,
    awayTeamId: game?.awayTeamId || game?.awayTeam?.id || null,
    leagueId: null,
    scheduledDate: game?.scheduledDate,
    venue: game?.venue,
    homeScore: game?.homeScore,
    awayScore: game?.awayScore,
    status: game?.gameStatus || 'scheduled',
    notes: game?.matchNotes || '',
    label: game?.stageName || 'Tournament Game',
  });

  const fetchFixtures = useCallback(async () => {
    setFixturesLoading(true);
    setFixturesError(null);
    setSelectedGameId('');
    try {
      if (matchType === 'tournament') {
        const games = await tournamentService.getGames();
        let list = (games || []).map(normalizeTournamentFixture);
        if (fixtureDate) {
          list = list.filter((g) => {
            if (!g.scheduledDate) return false;
            const d = new Date(g.scheduledDate);
            const start = new Date(fixtureDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(fixtureDate);
            end.setHours(23, 59, 59, 999);
            return d >= start && d <= end;
          });
        }
        list.sort((a, b) => {
          const aHas = a.homeScore != null && a.awayScore != null;
          const bHas = b.homeScore != null && b.awayScore != null;
          if (aHas && !bHas) return -1;
          if (!aHas && bHas) return 1;
          return new Date(b.scheduledDate || 0) - new Date(a.scheduledDate || 0);
        });
        setFixtures(list.slice(0, 40));
      } else {
        const data = await matchService.getRecentFixtures({
          limit: 40,
          fixtureDate: fixtureDate || undefined,
        });
        setFixtures((data || []).map(normalizeLeagueFixture));
      }
    } catch (error) {
      setFixturesError(error?.message || 'Failed to load fixtures.');
      setFixtures([]);
    } finally {
      setFixturesLoading(false);
    }
  }, [matchType, fixtureDate]);

  useEffect(() => {
    fetchFixtures();
  }, [fetchFixtures]);

  const gameOptions = useMemo(
    () =>
      fixtures.map((fixture) => {
        const score =
          fixture.homeScore != null && fixture.awayScore != null
            ? ` (${fixture.homeScore}-${fixture.awayScore})`
            : '';
        const when = fixture.scheduledDate
          ? new Date(fixture.scheduledDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })
          : 'TBA';
        return {
          value: String(fixture.id),
          label: `${when} · ${fixture.homeName} vs ${fixture.awayName}${score}`,
        };
      }),
    [fixtures]
  );

  const recentCompleted = useMemo(
    () =>
      fixtures.filter(
        (f) =>
          f.status === 'completed' ||
          (f.homeScore != null && f.awayScore != null)
      ),
    [fixtures]
  );

  const openEditModal = (match) => {
    if (!match) return;

    let homeScore = '';
    let awayScore = '';

    if (match.score && typeof match.score === 'string' && match.score.includes('-')) {
      const scoreParts = match.score.split('-').map((val) => val.trim());
      homeScore = scoreParts[0] || '';
      awayScore = scoreParts[1] || '';
    } else if (match.homeScore !== undefined && match.homeScore !== null) {
      homeScore = String(match.homeScore);
      awayScore = match.awayScore != null ? String(match.awayScore) : '';
    }

    setSelectedMatch(match);
    setEditForm({
      homeScore,
      awayScore,
      status: match.status || 'completed',
      notes: match.notes || '',
    });
    setEditModalOpen(true);
    setBanner(null);
  };

  const handleSelectFixture = (fixture) => {
    if (!fixture) return;
    const fixtureDateLabel = `${formatFixtureDate(fixture.scheduledDate)}${
      fixture.venue ? ` • ${fixture.venue}` : ''
    }`;
    const hasOfficialScore = fixture.homeScore != null && fixture.awayScore != null;

    openEditModal({
      id: fixture.id,
      matchType: fixture.matchType,
      home: fixture.homeName,
      away: fixture.awayName,
      date: fixtureDateLabel,
      score: hasOfficialScore ? `${fixture.homeScore} - ${fixture.awayScore}` : '0 - 0',
      status: fixture.status || 'scheduled',
      notes: fixture.notes || '',
      homeScore: fixture.homeScore,
      awayScore: fixture.awayScore,
    });
  };

  const handleMatchTypeChange = (value) => {
    setMatchType(value);
    setSelectedGameId('');
    setFixtureDate('');
  };

  const handleGameSelect = (value) => {
    setSelectedGameId(value);
  };

  const selectedFixture = useMemo(
    () => fixtures.find((item) => String(item.id) === String(selectedGameId)) || null,
    [fixtures, selectedGameId]
  );

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedMatch(null);
    setEditForm({
      homeScore: '',
      awayScore: '',
      status: 'completed',
      notes: '',
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveMatch = async (event) => {
    event?.preventDefault();
    if (!selectedMatch?.id) {
      setBanner({
        type: 'error',
        message: 'Invalid match selected. Please choose a league or tournament game.',
      });
      return;
    }

    setIsSaving(true);
    setBanner(null);
    try {
      const homeScore = editForm.homeScore !== '' ? parseInt(editForm.homeScore, 10) : null;
      const awayScore = editForm.awayScore !== '' ? parseInt(editForm.awayScore, 10) : null;
      const finalStatus =
        homeScore !== null && awayScore !== null
          ? 'completed'
          : editForm.status || 'scheduled';

      if (selectedMatch.matchType === 'tournament') {
        await tournamentService.updateGame(selectedMatch.id, {
          homeScore: homeScore ?? 0,
          awayScore: awayScore ?? 0,
          gameStatus: finalStatus,
        });
      } else {
        const now = new Date().toISOString();
        await matchService.updateScore(selectedMatch.id, {
          homeScore,
          awayScore,
          matchStatus: finalStatus,
          matchNotes: editForm.notes || null,
          endedAt: finalStatus === 'completed' ? now : null,
        });
      }

      await fetchFixtures();
      setBanner({
        type: 'success',
        message:
          selectedMatch.matchType === 'tournament'
            ? 'Tournament game result saved successfully.'
            : 'Match results saved successfully. League standings and team statistics have been updated.',
      });

      setTimeout(() => {
        closeEditModal();
      }, 1000);
    } catch (error) {
      console.error('Error saving match:', error);
      setBanner({
        type: 'error',
        message: error?.message || 'Failed to save match results to database.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const statusBadge = (fixture) => {
    const hasScores = fixture.homeScore != null && fixture.awayScore != null;
    const actualStatus = hasScores ? 'completed' : fixture.status || 'scheduled';
    const statusColors = {
      completed: 'bg-success/10 text-success',
      live: 'bg-error/10 text-error',
      scheduled: 'bg-primary/10 text-primary',
      postponed: 'bg-warning/10 text-warning',
      cancelled: 'bg-muted text-muted-foreground',
    };
    const displayStatus = actualStatus.charAt(0).toUpperCase() + actualStatus.slice(1);
    return (
      <span
        className={`text-xs px-2 py-1 rounded-full capitalize ${
          statusColors[actualStatus] || 'bg-muted text-muted-foreground'
        }`}
      >
        {displayStatus}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main
        className={`pt-16 transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="p-6 space-y-6">
          <div className="flex flex-col space-y-4">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="ClipboardList" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Matches Updates</h1>
                  <p className="text-muted-foreground">
                    Choose league matches or tournament games, then record official results.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                iconName="RefreshCcw"
                onClick={fetchFixtures}
                loading={fixturesLoading}
                disabled={fixturesLoading}
              >
                Refresh List
              </Button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg card-shadow p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/10 rounded-lg">
                <Icon name="CalendarClock" size={20} className="text-success" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Select Fixture to Record</h2>
                <p className="text-sm text-muted-foreground">
                  Pick match type and a game. Use Record Result for the score, or select a fixture to
                  enter player stats below.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Match Type"
                options={MATCH_TYPE_OPTIONS}
                value={matchType}
                onChange={handleMatchTypeChange}
                placeholder="Select match type"
              />
              <Select
                label={matchType === 'tournament' ? 'Tournament Game' : 'League Match'}
                options={gameOptions}
                value={selectedGameId}
                onChange={handleGameSelect}
                searchable
                placeholder={
                  fixturesLoading
                    ? 'Loading games...'
                    : gameOptions.length === 0
                      ? `No ${matchType === 'tournament' ? 'tournament' : 'league'} games found`
                      : 'Select game to record'
                }
                disabled={fixturesLoading || gameOptions.length === 0}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide">
                  Optional Date Filter
                </label>
                <input
                  type="date"
                  value={fixtureDate}
                  onChange={(e) => setFixtureDate(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </div>
              <div className="flex items-end gap-2 md:col-span-2">
                {(fixtureDate || selectedGameId) && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setFixtureDate('');
                      setSelectedGameId('');
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>

            <div className="divide-y divide-border">
              {fixturesLoading && (
                <div className="py-10 text-center text-muted-foreground space-y-2">
                  <Icon name="Loader2" size={32} className="mx-auto animate-spin" />
                  <p>
                    Loading {matchType === 'tournament' ? 'tournament games' : 'league matches'}...
                  </p>
                </div>
              )}
              {!fixturesLoading && fixturesError && (
                <div className="py-8 text-center text-destructive space-y-2">
                  <Icon name="AlertTriangle" size={28} className="mx-auto" />
                  <p>{fixturesError}</p>
                  <Button variant="outline" size="sm" iconName="RefreshCcw" onClick={fetchFixtures}>
                    Retry
                  </Button>
                </div>
              )}
              {!fixturesLoading && !fixturesError && fixtures.length === 0 && (
                <div className="py-8 text-center text-muted-foreground space-y-2">
                  <Icon name="CalendarX" size={28} className="mx-auto" />
                  <p>
                    No {matchType === 'tournament' ? 'tournament games' : 'league matches'} found.
                    Try another type or clear the date filter.
                  </p>
                </div>
              )}
              {!fixturesLoading &&
                !fixturesError &&
                fixtures.map((fixture) => (
                  <div
                    key={fixture.id}
                    className={`py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${
                      String(selectedGameId) === String(fixture.id) ? 'bg-primary/5 -mx-2 px-2 rounded-lg' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="text-left min-w-0 flex-1"
                      onClick={() => setSelectedGameId(String(fixture.id))}
                    >
                      <p className="text-xs uppercase tracking-wide text-primary font-semibold">
                        {fixture.label}
                      </p>
                      <p className="text-lg font-semibold text-foreground">
                        {fixture.homeName} vs {fixture.awayName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatFixtureDate(fixture.scheduledDate)}
                        {fixture.venue ? ` • ${fixture.venue}` : ''}
                      </p>
                    </button>
                    <div className="flex items-center gap-2">
                      {fixture.homeScore != null && fixture.awayScore != null && (
                        <span className="text-sm font-semibold text-foreground">
                          {fixture.homeScore} - {fixture.awayScore}
                        </span>
                      )}
                      {statusBadge(fixture)}
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="FileEdit"
                        onClick={() => {
                          setSelectedGameId(String(fixture.id));
                          handleSelectFixture(fixture);
                        }}
                      >
                        {fixture.homeScore != null && fixture.awayScore != null
                          ? 'Update Result'
                          : 'Record Result'}
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {selectedFixture ? (
            <PlayerMatchStatsRecorder
              key={`${selectedFixture.matchType}-${selectedFixture.id}`}
              fixture={selectedFixture}
            />
          ) : (
            <div className="bg-card border border-dashed border-border rounded-lg p-8 text-center text-muted-foreground">
              <Icon name="ClipboardList" size={32} className="mx-auto mb-3 opacity-60" />
              <p className="font-medium text-foreground mb-1">Player match stats</p>
              <p className="text-sm">
                Select a fixture above to manually record individual player statistics for both teams.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-lg card-shadow">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Recent Completed</h2>
                  <p className="text-sm text-muted-foreground">
                    {matchType === 'tournament' ? 'Tournament' : 'League'} games with recorded scores.
                  </p>
                </div>
                <Icon name="ListChecks" size={20} className="text-muted-foreground" />
              </div>
              <div className="divide-y divide-border">
                {recentCompleted.length === 0 && (
                  <div className="p-6 text-sm text-muted-foreground text-center">
                    No completed results in the current list yet.
                  </div>
                )}
                {recentCompleted.map((match) => (
                  <div
                    key={match.id}
                    className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  >
                    <div>
                      <p className="font-medium text-foreground">
                        {match.homeName} vs {match.awayName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatFixtureDate(match.scheduledDate)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-foreground">
                        {match.homeScore ?? 0} - {match.awayScore ?? 0}
                      </p>
                      {statusBadge(match)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Edit"
                      onClick={() => handleSelectFixture(match)}
                    >
                      Edit
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg card-shadow p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="PenSquare" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Update Toolkit</h2>
                  <p className="text-sm text-muted-foreground">
                    Fast actions and reminders for post-game workflow.
                  </p>
                </div>
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
                  Select League Matches or Tournament Games above.
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="TrendingUp" size={16} className="text-primary mt-0.5" />
                  Choose the specific fixture from the second dropdown.
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="UploadCloud" size={16} className="text-warning mt-0.5" />
                  Enter final scores and notes, then save.
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Bell" size={16} className="text-secondary mt-0.5" />
                  League saves also refresh standings where applicable.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {editModalOpen && selectedMatch && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-lg w-full card-shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {selectedMatch.matchType === 'tournament'
                    ? 'Edit Tournament Game'
                    : 'Edit League Match'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedMatch.home} vs {selectedMatch.away} — {selectedMatch.date}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={closeEditModal}>
                <Icon name="X" size={18} />
              </Button>
            </div>
            <form onSubmit={handleSaveMatch} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">
                    Home Score
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.homeScore}
                    onChange={(e) => handleEditFormChange('homeScore', e.target.value)}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">
                    Away Score
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.awayScore}
                    onChange={(e) => handleEditFormChange('awayScore', e.target.value)}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide">
                  Match Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => handleEditFormChange('status', e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                >
                  <option value="completed">Completed</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="live">Live</option>
                  <option value="postponed">Postponed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {selectedMatch.matchType !== 'tournament' && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">
                    Notes / Highlights
                  </label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => handleEditFormChange('notes', e.target.value)}
                    rows={4}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                    placeholder="Add key statistics, MVP mention, injuries, overtime details..."
                  />
                </div>
              )}

              {banner && (
                <div
                  className={`px-3 py-2 rounded text-sm ${
                    banner.type === 'success'
                      ? 'bg-success/10 text-success'
                      : 'bg-destructive/10 text-destructive'
                  }`}
                >
                  {banner.message}
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" onClick={closeEditModal} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" iconName="Save" loading={isSaving} disabled={isSaving}>
                  Save Updates
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchesUpdatesPage;
